import { useState } from "react";
import { useNavigate } from "react-router-dom";
import { toast } from "sonner";
import { apiClient, formatApiError } from "@/lib/api";
import { useCart } from "@/contexts/CartContext";

export default function Checkout() {
  const { items, subtotal } = useCart();
  const navigate = useNavigate();
  const [loading, setLoading] = useState(false);
  const [form, setForm] = useState({
    customer_name: "",
    customer_email: "",
    line1: "",
    line2: "",
    city: "",
    state: "",
    postal_code: "",
    country: "US",
  });

  if (items.length === 0) {
    return (
      <div className="max-w-[800px] mx-auto px-6 py-20 text-center" data-testid="checkout-empty">
        <h1 className="font-display text-4xl uppercase tracking-tight">Cart is empty</h1>
        <p className="text-[#9ca3af] mt-3">Add something before you check out.</p>
        <button onClick={() => navigate("/shop")} className="btn-primary mt-8" data-testid="checkout-empty-shop">
          GO TO SHOP
        </button>
      </div>
    );
  }

  const shipping = subtotal >= 150 ? 0 : 12;
  const total = subtotal + shipping;

  function set(k, v) {
    setForm((f) => ({ ...f, [k]: v }));
  }

  async function handleSubmit(e) {
    e.preventDefault();
    if (!form.customer_email || !form.customer_name || !form.line1 || !form.city || !form.state || !form.postal_code) {
      toast.error("Please fill all required fields");
      return;
    }
    setLoading(true);
    try {
      const payload = {
        items: items.map((it) => ({ product_id: it.product_id, quantity: it.quantity, variant: it.variant || null })),
        origin_url: window.location.origin,
        customer_email: form.customer_email,
        customer_name: form.customer_name,
        shipping_address: {
          line1: form.line1,
          line2: form.line2,
          city: form.city,
          state: form.state,
          postal_code: form.postal_code,
          country: form.country,
        },
      };
      const { data } = await apiClient.post("/checkout/session", payload);
      if (data?.url) {
        window.location.href = data.url;
      } else {
        toast.error("Could not start checkout");
      }
    } catch (err) {
      toast.error(formatApiError(err));
    } finally {
      setLoading(false);
    }
  }

  return (
    <div className="max-w-[1400px] mx-auto px-6 lg:px-10 py-16" data-testid="checkout-page">
      <div className="bgc-eyebrow text-[#c8102e]">CHECKOUT</div>
      <h1 className="font-display text-6xl tracking-tight uppercase mt-3">Almost There.</h1>

      <form onSubmit={handleSubmit} className="grid grid-cols-1 lg:grid-cols-3 gap-10 mt-12">
        <div className="lg:col-span-2 space-y-8">
          <section>
            <h2 className="font-display text-2xl tracking-wider uppercase mb-5">Contact</h2>
            <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
              <Field label="Full name" required>
                <input data-testid="checkout-name" className="input-dark w-full" value={form.customer_name} onChange={(e) => set("customer_name", e.target.value)} />
              </Field>
              <Field label="Email" required>
                <input data-testid="checkout-email" type="email" className="input-dark w-full" value={form.customer_email} onChange={(e) => set("customer_email", e.target.value)} />
              </Field>
            </div>
          </section>

          <section>
            <h2 className="font-display text-2xl tracking-wider uppercase mb-5">Shipping Address</h2>
            <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
              <Field label="Street address" required className="md:col-span-2">
                <input data-testid="checkout-line1" className="input-dark w-full" value={form.line1} onChange={(e) => set("line1", e.target.value)} />
              </Field>
              <Field label="Apt / Suite (optional)" className="md:col-span-2">
                <input data-testid="checkout-line2" className="input-dark w-full" value={form.line2} onChange={(e) => set("line2", e.target.value)} />
              </Field>
              <Field label="City" required>
                <input data-testid="checkout-city" className="input-dark w-full" value={form.city} onChange={(e) => set("city", e.target.value)} />
              </Field>
              <Field label="State" required>
                <input data-testid="checkout-state" className="input-dark w-full" value={form.state} onChange={(e) => set("state", e.target.value)} />
              </Field>
              <Field label="ZIP" required>
                <input data-testid="checkout-zip" className="input-dark w-full" value={form.postal_code} onChange={(e) => set("postal_code", e.target.value)} />
              </Field>
              <Field label="Country">
                <input data-testid="checkout-country" className="input-dark w-full" value={form.country} onChange={(e) => set("country", e.target.value)} />
              </Field>
            </div>
          </section>
        </div>

        <aside className="border border-white/10 p-6 h-fit bg-[#0a0a0a]">
          <h2 className="font-display text-2xl tracking-wider uppercase">Order</h2>
          <ul className="mt-4 space-y-3 max-h-[260px] overflow-y-auto">
            {items.map((it, i) => (
              <li key={i} className="flex justify-between text-sm">
                <span className="text-[#d1d5db]">{it.name} × {it.quantity}</span>
                <span>${(it.price * it.quantity).toFixed(2)}</span>
              </li>
            ))}
          </ul>
          <div className="border-t border-white/10 mt-5 pt-4 space-y-2 text-sm">
            <div className="flex justify-between"><span className="text-[#9ca3af]">Subtotal</span><span>${subtotal.toFixed(2)}</span></div>
            <div className="flex justify-between"><span className="text-[#9ca3af]">Shipping</span><span>{shipping === 0 ? "FREE" : `$${shipping.toFixed(2)}`}</span></div>
            <div className="flex justify-between font-bold pt-3 border-t border-white/10"><span>Total</span><span data-testid="checkout-total">${total.toFixed(2)}</span></div>
          </div>
          <button type="submit" disabled={loading} className="btn-primary w-full justify-center mt-6" data-testid="checkout-pay-button">
            {loading ? "REDIRECTING…" : `PAY $${total.toFixed(2)}`}
          </button>
          <p className="text-xs text-[#6b7280] mt-3 text-center">Secure payment via Stripe</p>
        </aside>
      </form>
    </div>
  );
}

function Field({ label, required, children, className = "" }) {
  return (
    <label className={`block ${className}`}>
      <span className="bgc-eyebrow block mb-2">
        {label} {required && <span className="text-[#c8102e]">*</span>}
      </span>
      {children}
    </label>
  );
}
